import type { NextPage, GetServerSidePropsContext } from 'next'
import { Heading, Box, Image, Flex, useColorModeValue } from '@chakra-ui/react'
import ReactMarkdown from 'react-markdown'

import { addApolloState, initializeApollo } from '@/utils/apolloClient'
import { getStrapiMedia } from '@/utils/getStrapiMedia'
import { getAboutPageData } from '@/api/strapi/aboutPage'
import {
  IAboutPage,
  IDescriptionCard,
} from '@/types/strapi/aboutPage.interface'
import { ITeamMember } from '@/types/teamMember.interface'

import { ContainerBase } from '@/components/containers/ContainerBase'
import { CardWithIcon } from '@/components/cards/CardWithIcon'
import { EmployeeCard } from '@/components/cards/EmployeeCard'
import SeoHead from '@/components/seo-head'
import { useTranslations } from 'next-intl'

type Props = {
  aboutPageData: IAboutPage
}

const AboutPage: NextPage<Props> = ({ aboutPageData }) => {
  const {
    title,
    mainImage,
    descriptionTitle,
    descriptionText,
    descriptionCards,
    ourTeamTitle,
    staff,
  } = aboutPageData
  const t = useTranslations()
  return (
    <>
      <SeoHead
        title={t('about.seo.title')}
        breadcrumbTitle={t('about.seo.breadcrumb')}
        description={t('about.seo.description')}
      />
      <Box
        bg={useColorModeValue('gray.50', 'gray.700')}
        py={{ base: 8, lg: 16 }}
      >
        <ContainerBase>
          {title && (
            <Heading
              as={'h1'}
              size={'3xl'}
              textAlign={'center'}
              mb={{ base: 8, lg: 16 }}
            >
              {title}
            </Heading>
          )}
          {mainImage && (
            <Image
              src={getStrapiMedia(mainImage.url)}
              alt={mainImage.alternativeText || title}
              w={'full'}
              borderTopRadius={'2xl'}
            />
          )}
        </ContainerBase>
      </Box>
      <Box
        shadow={'containerSection'}
        bgGradient={useColorModeValue(
          'linear-gradient(283.35deg, #F9F9F9 7.06%, #FEFEFE 96.82%)',
          'linear-gradient(283.35deg, #090909 7.06%, #0E0E0E 96.82%)'
        )}
        py={{ base: 8, lg: 16 }}
      >
        <ContainerBase>
          <Box maxW={'container.md'}>
            {descriptionTitle && (
              <Heading as={'h2'} size={'3xl'} mb={{ base: 4, lg: 5 }}>
                {descriptionTitle}
              </Heading>
            )}
            {descriptionText && (
              <Box fontSize={'md'} fontWeight={400} mb={{ base: 8, lg: 16 }}>
                <ReactMarkdown>{descriptionText}</ReactMarkdown>
              </Box>
            )}
          </Box>
          {descriptionCards?.length && (
            <Flex
              wrap={'wrap'}
              direction={{ base: 'column', md: 'row' }}
              gap={16}
            >
              {descriptionCards.map((card: IDescriptionCard) => (
                <Flex
                  w={{ base: '100%', md: '326px' }}
                  key={`description_card_${card.id}`}
                  flex={1}
                >
                  <CardWithIcon
                    description={card.description}
                    icon={card.iconImage ? card.iconImage.url : null}
                    title={card.title}
                  />
                </Flex>
              ))}
            </Flex>
          )}
        </ContainerBase>
      </Box>
      {staff && staff.length && (
        <Box py={{ base: 8, lg: 16 }}>
          <ContainerBase>
            {ourTeamTitle && (
              <Heading as={'h2'} size={'3xl'} mb={{ base: 4, lg: 10 }}>
                {ourTeamTitle}
              </Heading>
            )}
            <Flex wrap={'wrap'} direction={'row'} mx={'auto'}>
              {staff.map((employee: ITeamMember) => (
                <Box
                  key={`employee_${employee.id}`}
                  w={{ base: 'full', xs: '50%', md: '33.3%', lg: '25%' }}
                  px={3}
                  mb={8}
                >
                  <EmployeeCard data={employee} />
                </Box>
              ))}
            </Flex>
          </ContainerBase>
        </Box>
      )}
    </>
  )
}

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query({
    query: getAboutPageData,
    variables: {
      locale,
    },
    context: {
      clientName: 'strapi',
    },
  })

  if (!data) {
    return addApolloState(apolloClient, {
      notFound: true,
    })
  }

  const aboutPageData = data.aboutPage || {}

  return addApolloState(apolloClient, {
    props: {
      aboutPageData,
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  })
}

export default AboutPage
